import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Pencil, Search, X } from 'lucide-react'
import type { ReactNode } from 'react'

import type { OnlinePriceItem } from '@/lib/api/online-price-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MoneyPreview } from '@/components/ui/money-preview'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useClearOnlinePrice,
    useOnlinePrices,
    useSetOnlinePrice,
} from '@/hooks/use-online-price-query'
import { MAX_ONLINE_PRICE } from '@/lib/api/online-price-api'
import { formatDateTime, formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// Bảng giá Online: giá gồm VAT bán trên website chetancuonghaodat.vn. Không đụng
// giá sản phẩm ERP; SKU chưa đặt giá Online bán theo giá ERP + VAT.
export const Route = createFileRoute(
    '/_app/_authed/_sidebar/retail/online-prices',
)({
    component: OnlinePricesPage,
})

export function OnlinePricesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('online_prices.write')
    const query = useOnlinePrices()
    const [search, setSearch] = useState('')
    const [editingId, setEditingId] = useState<string | null>(null)

    const list = query.data?.data
    const rows = filterRows(list?.items ?? [], search)
    const colCount = canWrite ? 7 : 6

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.online_price_title()}
                subtitle={m.online_price_subtitle({
                    vat: String(list?.vat_percent ?? 0),
                })}
            />

            <div className="relative max-w-sm">
                <Search
                    className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={m.online_price_search()}
                    aria-label={m.online_price_search()}
                    className="pl-8"
                />
            </div>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-36">
                                {m.online_price_col_sku()}
                            </TableHead>
                            <TableHead className="min-w-48">
                                {m.online_price_col_name()}
                            </TableHead>
                            <TableHead className="w-32 text-right">
                                {m.online_price_col_erp()}
                            </TableHead>
                            <TableHead className="w-56 text-right">
                                {m.online_price_col_online()}
                            </TableHead>
                            <TableHead className="w-40 text-right">
                                {m.online_price_col_web()}
                            </TableHead>
                            <TableHead className="w-44">
                                {m.online_price_col_updated()}
                            </TableHead>
                            {canWrite && <TableHead className="w-24" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isLoading ? (
                            <MessageRow colSpan={colCount}>
                                {m.online_price_loading()}
                            </MessageRow>
                        ) : query.isError ? (
                            <MessageRow colSpan={colCount} destructive>
                                {m.online_price_load_failed()}
                            </MessageRow>
                        ) : rows.length === 0 ? (
                            <MessageRow colSpan={colCount}>
                                {m.online_price_empty()}
                            </MessageRow>
                        ) : (
                            rows.map((row) => (
                                <PriceRow
                                    key={row.product_id}
                                    row={row}
                                    canWrite={canWrite}
                                    editing={editingId === row.product_id}
                                    onEdit={() => setEditingId(row.product_id)}
                                    onDone={() => setEditingId(null)}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {rows.length > 0 && (
                <p className="text-xs text-muted-foreground">
                    {m.online_price_total({ count: String(rows.length) })}
                </p>
            )}
        </div>
    )
}

function MessageRow({
    colSpan,
    destructive,
    children,
}: {
    colSpan: number
    destructive?: boolean
    children: ReactNode
}) {
    return (
        <TableRow>
            <TableCell
                colSpan={colSpan}
                className={
                    destructive
                        ? 'py-8 text-center text-destructive'
                        : 'py-8 text-center text-muted-foreground'
                }
            >
                {children}
            </TableCell>
        </TableRow>
    )
}

function PriceRow({
    row,
    canWrite,
    editing,
    onEdit,
    onDone,
}: {
    row: OnlinePriceItem
    canWrite: boolean
    editing: boolean
    onEdit: () => void
    onDone: () => void
}) {
    const clearMut = useClearOnlinePrice()
    const hasOnline = row.online_price !== null

    const handleClear = () => {
        if (!window.confirm(m.online_price_clear_confirm({ sku: row.sku }))) {
            return
        }
        clearMut.mutate(row.product_id)
    }

    return (
        <TableRow>
            <TableCell className="font-mono">{row.sku}</TableCell>
            <TableCell>
                {row.name}
                <span className="ml-1 text-xs text-muted-foreground">
                    ({row.unit})
                </span>
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatVND(row.erp_price_incl_vat)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {editing ? (
                    <PriceEditor row={row} onDone={onDone} />
                ) : hasOnline ? (
                    formatVND(row.online_price ?? 0)
                ) : (
                    <span className="text-muted-foreground">
                        {m.online_price_none()}
                    </span>
                )}
            </TableCell>
            <TableCell className="text-right">
                <div className="flex flex-col items-end gap-1">
                    <span className="tabular-nums font-medium">
                        {formatVND(row.price_incl_vat)}
                    </span>
                    <Badge variant={hasOnline ? 'default' : 'secondary'}>
                        {hasOnline
                            ? m.online_price_source_online()
                            : m.online_price_source_erp()}
                    </Badge>
                </div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
                {row.updated_at ? (
                    <>
                        {formatDateTime(row.updated_at)}
                        {row.updated_by_name && (
                            <div>{row.updated_by_name}</div>
                        )}
                    </>
                ) : (
                    '—'
                )}
            </TableCell>
            {canWrite && (
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {!editing && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onEdit}
                            aria-label={m.online_price_edit({ sku: row.sku })}
                            title={m.online_price_edit({ sku: row.sku })}
                        >
                            <Pencil className="size-4" />
                        </Button>
                    )}
                    {!editing && hasOnline && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleClear}
                            disabled={clearMut.isPending}
                            aria-label={m.online_price_clear({ sku: row.sku })}
                            title={m.online_price_clear({ sku: row.sku })}
                        >
                            <X className="size-4 text-red-500" />
                        </Button>
                    )}
                </TableCell>
            )}
        </TableRow>
    )
}

/** Export để test riêng ô sửa giá mà không phải dựng cả router. */
export function PriceEditor({
    row,
    onDone,
}: {
    row: OnlinePriceItem
    onDone: () => void
}) {
    const setMut = useSetOnlinePrice()
    const [raw, setRaw] = useState(() =>
        String(row.online_price ?? row.erp_price_incl_vat),
    )
    // Lỗi hiện dưới ô, không khoá nút Lưu: bấm Lưu luôn được và luôn biết vì sao
    // không lưu (quy tắc form của repo).
    const [error, setError] = useState<string | null>(null)
    const inputId = `online-price-${row.product_id}`
    const previewId = `${inputId}-preview`

    const submit = async () => {
        const parsed = parsePrice(raw)
        if (typeof parsed === 'string') {
            setError(parsed)
            return
        }
        setError(null)
        try {
            await setMut.mutateAsync({
                productId: row.product_id,
                price: parsed,
            })
            onDone()
        } catch {
            // Toast hiện qua onError của mutation — giữ ô để sửa tiếp.
        }
    }

    return (
        <form
            // Tự kiểm và báo lỗi tiếng Việt dưới ô; kiểm tra gốc của trình duyệt
            // (step/min) sẽ chặn submit bằng bong bóng tiếng Anh.
            noValidate
            className="flex flex-col items-end gap-1"
            onSubmit={(e) => {
                e.preventDefault()
                void submit()
            }}
        >
            <div className="flex items-center gap-1">
                <label htmlFor={inputId} className="sr-only">
                    {m.online_price_input_label({ sku: row.sku })}
                </label>
                <Input
                    id={inputId}
                    type="number"
                    inputMode="numeric"
                    step={1}
                    min={1}
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') onDone()
                    }}
                    className="w-32 text-right"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={previewId}
                    autoFocus
                />
                <Button type="submit" size="sm" disabled={setMut.isPending}>
                    {setMut.isPending ? '...' : m.common_save()}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onDone}
                    disabled={setMut.isPending}
                >
                    {m.common_cancel()}
                </Button>
            </div>
            <MoneyPreview id={previewId} value={raw} />
            {error && (
                <p role="alert" className="text-xs text-destructive">
                    {error}
                </p>
            )}
        </form>
    )
}

/** Số nguyên đồng 1..MAX, hoặc câu lỗi tiếng Việt để hiện dưới ô. */
export function parsePrice(raw: string): number | string {
    const text = raw.trim()
    if (text === '') return m.online_price_error_required()
    const n = Number(text)
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
        return m.online_price_error_integer()
    }
    if (n < 1 || n > MAX_ONLINE_PRICE) {
        return m.online_price_error_range({ max: formatVND(MAX_ONLINE_PRICE) })
    }
    return n
}

export function filterRows(
    items: OnlinePriceItem[],
    search: string,
): OnlinePriceItem[] {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(
        (it) =>
            it.sku.toLowerCase().includes(q) ||
            it.name.toLowerCase().includes(q),
    )
}
